/**
 * Relationship Matrix evaluator.
 *
 * Reads the rule-based matrix file (registry/archimate-matrix.json) and
 * answers: "is relation R valid between source-type S and target-type T?"
 *
 * The matrix encodes, per relation:
 *   default: "allow" | "deny"
 *   allow_when: clauses that, when matched, override deny default
 *   deny_when: clauses that, when matched, override allow default
 *   explicit_pairs: list of [sourceTypeId, targetTypeId] for the
 *     `kind: "explicit_pairs"` clause.
 *   grouping_can_compose_anything / grouping_can_aggregate_anything: Grouping
 *     escape hatches.
 *
 * The runtime calls `validate(relationTypeId, sourceType, targetType)` where
 * `sourceType` and `targetType` are objects with `id`, `layer`, and `aspect`.
 * Same shape as the registry element-type definition.
 */

import { officialTableAllows } from "./archimateRelationTables";

export interface TypeRef {
  id: string;
  layer: string;
  aspect: string;
}

interface AllowDenyClause {
  kind?:
    | "same_type"
    | "same_layer"
    | "same_aspect"
    | "same_layer_same_aspect"
    | "explicit_pairs"
    | "deny_pairs";
  source_layer?: string;
  target_layer?: string;
  source_aspect?: string;
  target_aspect?: string;
}

interface RelationRule {
  description?: string;
  default: "allow" | "deny";
  allow_when?: AllowDenyClause[];
  deny_when?: AllowDenyClause[];
  /**
   * Paren die nooit mogen, ook al laat een `allow_when`-clausule ze door.
   * Gebruikt met `{ kind: "deny_pairs" }` in `deny_when`.
   *
   * Nodig omdat een laag/aspect-clausule te grof kan zijn: de
   * Common-Domain-brug voor Aggregation laat elk common-element samengaan met
   * elk element van hetzelfde aspect, en dat is voor Role en Collaboration
   * precies goed maar voor Path niet meer — zie C260 Appendix E.4.
   */
  deny_pairs?: Array<[string, string]>;
  explicit_pairs?: [string, string][];
  grouping_can_compose_anything?: boolean;
  grouping_can_aggregate_anything?: boolean;
}

interface MatrixFile {
  pack_id: string;
  matrix_version: string;
  rules: Record<string, RelationRule>;
}

export type ValidationOutcome = { valid: true } | { valid: false; reason: string };

export class MatrixValidator {
  private readonly rules: Map<string, RelationRule>;

  /**
   * Accepts either a single matrix-file or an array of them. Multiple
   * matrices have their `rules` maps merged in order — later entries win
   * on `default` / `allow_when` / `deny_when` (so a downstream pack can
   * still flat-out replace an upstream rule), BUT `explicit_pairs` is
   * APPENDED rather than overwritten. That way a downstream pack like
   * `c4` can add new allowed source/target combinations to an existing
   * `archimate.Flow` rule without having to copy the whole rule body.
   * Single-matrix construction stays backward-compatible with the
   * previous `new MatrixValidator(file)` signature.
   */
  constructor(matrix: unknown | unknown[]) {
    this.rules = new Map();
    const files = Array.isArray(matrix) ? (matrix as unknown[]) : [matrix];
    for (const m of files) {
      const file = m as MatrixFile;
      for (const [k, v] of Object.entries(file.rules ?? {})) {
        const existing = this.rules.get(k);
        if (!existing) {
          this.rules.set(k, v);
          continue;
        }
        // Concatenate pairs — later pack adds to the upstream allowlist.
        // For everything else last-write-wins (default/allow/deny/flags
        // override; `description` from the latest pack is fine to lose).
        const mergedPairs = [...(existing.explicit_pairs ?? []), ...(v.explicit_pairs ?? [])];
        this.rules.set(k, {
          ...existing,
          ...v,
          ...(mergedPairs.length > 0 ? { explicit_pairs: mergedPairs } : {}),
        });
      }
    }
  }

  validate(relationTypeId: string, source: TypeRef, target: TypeRef): ValidationOutcome {
    const rule = this.rules.get(relationTypeId);
    if (!rule) {
      return { valid: false, reason: `unknown relation type '${relationTypeId}'` };
    }

    // **Een tekenelement draagt alleen tekenrelaties.**
    //
    // `drawing.Rectangle` en z'n broers zijn niet-semantisch: het zijn vlakken
    // op het doek, geen dingen in het model. Een `Serving` of `Association`
    // vanaf zo'n vlak beweert iets wat het model niet kán bedoelen — en die
    // bewering reist mee: de relatietabellen in, de validatie in, de
    // AMEFF-export in. Dat gebeurde tot 2026-09-02, omdat `Association` op
    // `default: allow` staat en `Serving` de laag-clausules toevallig haalde.
    //
    // Hier hard, niet als waarschuwing: de matrix is de plek waar de taal
    // grenzen stelt, en dit is er één. De andere kant blijft zoals 'ie was —
    // `drawing.Line` en `drawing.ArrowLine` mogen tussen álles, want ze
    // beweren niets.
    const raaktTekening = isDrawingTypeId(source.id) || isDrawingTypeId(target.id);
    if (raaktTekening && !isDrawingTypeId(relationTypeId)) {
      return {
        valid: false,
        reason: `relation '${relationTypeId}' not allowed: '${source.id}' or '${target.id}' is a drawing primitive, which carries only drawing relations`,
      };
    }

    const groupingId = "archimate.Grouping";
    const isComposition = relationTypeId === "archimate.Composition";
    const isAggregation = relationTypeId === "archimate.Aggregation";
    if (
      (rule.grouping_can_compose_anything && isComposition && source.id === groupingId) ||
      (rule.grouping_can_aggregate_anything && isAggregation && source.id === groupingId)
    ) {
      return { valid: true };
    }

    const explicitPairs = rule.explicit_pairs ?? [];
    const denyPairs = rule.deny_pairs ?? [];

    const matchesClause = (clause: AllowDenyClause): boolean => {
      if (clause.kind === "same_type") {
        return source.id === target.id;
      }
      if (clause.kind === "same_layer") {
        return source.layer === target.layer;
      }
      if (clause.kind === "same_aspect") {
        return source.aspect === target.aspect;
      }
      if (clause.kind === "same_layer_same_aspect") {
        return source.layer === target.layer && source.aspect === target.aspect;
      }
      if (clause.kind === "explicit_pairs") {
        return explicitPairs.some(([s, t]) => s === source.id && t === target.id);
      }
      if (clause.kind === "deny_pairs") {
        return denyPairs.some(([s, t]) => s === source.id && t === target.id);
      }
      // Aspect/layer attribute match — all specified attributes must align.
      if (clause.source_layer !== undefined && clause.source_layer !== source.layer) {
        return false;
      }
      if (clause.target_layer !== undefined && clause.target_layer !== target.layer) {
        return false;
      }
      if (clause.source_aspect !== undefined && clause.source_aspect !== source.aspect) {
        return false;
      }
      if (clause.target_aspect !== undefined && clause.target_aspect !== target.aspect) {
        return false;
      }
      // If at least one attribute was specified, this is a positive match.
      const hasAttribute =
        clause.source_layer !== undefined ||
        clause.target_layer !== undefined ||
        clause.source_aspect !== undefined ||
        clause.target_aspect !== undefined;
      return hasAttribute;
    };

    const allowMatch = rule.allow_when?.some(matchesClause) ?? false;
    const denyMatch = rule.deny_when?.some(matchesClause) ?? false;

    // Een weigering weegt altijd het zwaarst, ook wanneer de regel op `deny`
    // staat. Anders is `deny_when` daar stilzwijgend zonder werking — een val
    // waar je pas in loopt als je 'm nodig hebt: je schrijft de uitzondering,
    // de test blijft groen, en de relatie mag nog steeds.
    // De officiële relatietabellen van ArchiMate 4 (C260 Appendix B.5) als
    // extra toestemming. Onze eigen regels zijn een benadering die afgeleide
    // relaties niet kent — een collaboration die via een toewijzing een proces
    // realiseert bijvoorbeeld — en die keurde op een echt ArchiSurance-bestand
    // acht relaties af die de taal gewoon toestaat.
    //
    // Bewust een únie en geen vervanging: onze regels staan op 1658
    // combinaties méér toe dan de standaard, en die er in één keer uitgooien
    // zou bestaande modellen kunnen breken. Dat opruimen is een aparte,
    // bewuste stap (zie 05-backlog).
    //
    // Een expliciete weigering blijft wel altijd voorgaan; die staat er om
    // iets tegen te houden wat een ruimere regel doorlaat.
    const officieel = officialTableAllows(relationTypeId, source.id, target.id);

    let allowed: boolean;
    if (denyMatch) {
      allowed = false;
    } else if (rule.default === "allow") {
      allowed = true;
    } else {
      allowed = allowMatch || officieel;
    }

    if (allowed) {
      return { valid: true };
    }
    return {
      valid: false,
      reason: `relation '${relationTypeId}' not allowed between '${source.id}' and '${target.id}' (rule default=${rule.default}${denyMatch ? ", deny matched" : ""}${!allowMatch && rule.default === "deny" ? ", no allow matched" : ""})`,
    };
  }

  /** List relation type-ids that the matrix knows about. */
  listRelationTypeIds(): string[] {
    return [...this.rules.keys()];
  }
}

/** Tekenprimitieven en -relaties dragen allemaal de `drawing.`-voorvoegsel. */
function isDrawingTypeId(id: string): boolean {
  return id.startsWith("drawing.");
}
